export class PaginationQuery {
    /**
     * Sort direction descending
     *
     * @static
     * @readonly
     * @return {string} DESCENDING
     */
    static get DESCENDING() {
        return 'desc';
    }

    /**
     * Sort direction ascending
     *
     * @static
     * @readonly
     * @return {string} ASCENDING
     */
    static get ASCENDING() {
        return 'asc';
    }

    constructor() {
        /**
         * The raw pagination data for
         *
         * @property {Number} pagination
         */
        this.pagination = null;

        /**
         * A key/value set of filters.
         *
         * To set a filter use setFilter(key, value)
         *
         * @property {Number} filters
         */
        this.filters = {};

        /**
         * The current page
         *
         * @property {Number} page
         */
        this.page = 1;

        /**
         * The max number of items per page
         *
         * @property {Number} itemsPerPage
         */
        this.itemsPerPage = null;

        /**
         * Items are sorted by this field
         *
         * @property {string} sortField
         */
        this.sortField = null;

        /**
         * The direction by which items are sorted
         *
         * @property {string} sortDirection - asc | desc
         */
        this.sortDirection = PaginationQuery.ASCENDING;

        this.reset();
    }

    /**
     * Set a filter value
     *
     * Setting a filter value to null remove it when getting data
     *
     * @param {String} field - The field to sort by
     * @param {*} value - The value sort on
     */
    setFilter(field, value) {
        if (!value && this.getFilter(field)) {
            delete this.filters[field];
            return;
        }

        this.filters[field] = value;
    }

    /**
     * All items have been got
     *
     * @function isLastPage
     * @return {Boolean}
     */
    get isLastPage() {
        return (this.pagination && this.pagination.last_page === this.page);
    }

    /**
     * Get the total pages
     *
     * Returns null if no pagination set
     *
     * @readonly
     * @property {Number} totalPages
     */
    get totalPages() {
        return (this.pagination && this.pagination.last_page)
            ? this.pagination.last_page
            : null;
    }

    /**
     * Get the state of a current filter
     *
     * returns null if no field present
     *
     * @param {String} field - The field to get
     * @return {*}
     */
    getFilter(field) {
        return this.filters[field] || null;
    }

    /**
     * Set the page to get
     *
     * If no pagination exists the page is set and assumed valid.
     * If pagination has been set 'page' restricted to values between
     * 1 and `last_page`
     *
     * @function setPage
     * @param {Number} page - The page index to get
     */
    setPage(page) {
        if (this.pagination && page > this.pagination.last_page) {
            page = this.pagination.last_page;
        } else if (page < 1) {
            page = 1;
        }

        this.page = page;
    }

    /**
     * Move to previous page
     *
     * @function prevPage
     */
    prevPage() {
        this.setPage(this.page - 1);
    }

    /**
     * Move to next page
     *
     * @function nextPage
     */
    nextPage() {
        this.setPage(this.page + 1);
    }

    /**
     * Goto the last page
     *
     * Only works when pagination data exists.
     *
     * @function goToLastPage
     */
    goToLastPage() {
        if (!this.pagination) {
            return;
        }
        this.setPage(this.pagination.last_page);
    }

    /**
     * Goto first page
     *
     * @function goToFirstPage
     */
    goToFirstPage() {
        this.setPage(1);
    }

    /**
     * Get the filter, sort and paging data
     *
     * The return object contains:
     * filters - if any exist otherwise not returned
     * sort - if sortField is set otherwise not returned
     * sort.direction - if sortField
     * sort.field
     * per_page - if set
     * page
     *
     * @function getData
     * @return {Object}
     */

    get queryData() {
        const query = {
            page: this.page,
        };

        // Add the filters if any are set
        if (this.filters) {
            query.filters = this.filters;
        }

        // Set the sort data if a field is specified
        if (this.sortField) {
            query.sort = {
                field: this.sortField,
                direction: this.sortDirection,
            };
        }

        // If specified set the total items
        if (this.itemsPerPage) {
            query.per_page = this.itemsPerPage;
        }

        return query;
    }


    /**
     * Reset all the options
     *
     * Clear all options and reset to the first page
     *
     * @function reset
     */
    reset() {
        this.pagination = null;

        // Set of filters to use
        this.filters = {};

        // set first page
        this.page = 1;

        // Sorting options
        this.sortDirection = PaginationQuery.ASCENDING;

        // Sort field
        this.sortField = null;

        this.itemsPerPage = null;
    }

    /**
     * Clears filters for paginationQuery
     */
    clearFilters() {
        this.filters = {};
    }

    /**
     * Goto the last page
     *
     * Only works when pagination data exists.
     *
     * @function toggleDirection
     * @param {Boolean} backToFirstPage - resets to first page
     */
    toggleDirection(backToFirstPage = true) {
        this.sortDirection = (this.sortDirection === PaginationQuery.ASCENDING)
            ? PaginationQuery.DESCENDING
            : PaginationQuery.ASCENDING;

        if (backToFirstPage) {
            this.goToFirstPage();
        }
    }
}
